import { ReactElement, useState } from 'react';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import styles from './OptionGroupTab.module.scss';
import { EmptyStateButton, Spinner, Typography } from '@weareopenr/component-library';
import { IMenuItem, IOptionGroup } from 'src/types/menu.types';
import ModifierCategory from '../ModifierCategory';
import { CreateOptionGroupModal } from '../CreateOptionGroupModal/CreateOptionGroupModal';
import { useItem } from 'src/hooks/useItem/useItem';
import PriceSchemeSelector from '../PriceSchemeSelector';
import { useSelectedPriceScheme } from 'src/hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { FEATURE_FLAGS, ItemType } from '../../constants';
import { EditOptionGroupDetailsModal } from '../EditOptionGroupDetailsModal/EditOptionGroupDetailsModal';
import { EditOptionGroupPricingModal } from '../EditOptionGroupPricingModal';
import { EditOptionGroupExternalSystemsModal } from '../EditOptionGroupExternalSystemsModal/EditOptionGroupExternalSystemsModal';
import { AssignOptionGroupModal } from '../AssignOptionGroupModal/AssignOptionGroupModal';
import { useAuthWrapper } from '@weareopenr/authkit';
import { ActiveModal } from 'src/pages/OptionGroupDisplay/OptionGroupDisplay';

const Content = ({ item }: { item: IMenuItem }): ReactElement => {
  const { selectedPriceSchemeId, setSelectedPriceSchemeId } = useSelectedPriceScheme();
  const { featureFlags } = useAuthWrapper();
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);
  const [optionGroupToEdit, setOptionGroupToEdit] = useState<IOptionGroup>();

  const canAssignOptionGroups = featureFlags.includes(FEATURE_FLAGS.INDEPENDENT_OPTION_GROUPS);
  const groupTypeLabel = item?.type === ItemType.BUNDLE ? 'bundle' : 'modifier';

  const openModal = (modal: ActiveModal) => (og?: IOptionGroup) => {
    setOptionGroupToEdit(og);
    setActiveModal(modal);
  };
  const closeModal = () => setActiveModal(null);

  const closeAssignModalOpenCreateOptionGroupModal = () => openModal(ActiveModal.DETAILS)();

  const ranges = item.ranges.map((range) => range.range_id);

  return (
    <>
      <CreateOptionGroupModal
        open={activeModal === ActiveModal.DETAILS && !optionGroupToEdit}
        onClose={closeModal}
        item={{ itemId: item.itemId, type: item.type }}
        bundleRangeIds={ranges}
      />

      <EditOptionGroupDetailsModal
        item={item}
        isOpen={activeModal === ActiveModal.DETAILS && !!optionGroupToEdit}
        optionGroup={optionGroupToEdit}
        onClose={closeModal}
      />

      <EditOptionGroupPricingModal
        isOpen={activeModal === ActiveModal.PRICING}
        close={closeModal}
        itemId={item.itemId}
        optionGroup={optionGroupToEdit}
      />
      <EditOptionGroupExternalSystemsModal
        isOpen={activeModal === ActiveModal.CHANNELS}
        closeModal={closeModal}
        optionGroup={optionGroupToEdit}
        itemId={item.itemId}
      />

      {canAssignOptionGroups && (
        <AssignOptionGroupModal
          open={activeModal === ActiveModal.ASSIGN}
          closeModal={closeModal}
          itemId={item.itemId}
          itemName={item.name}
          itemType={item.type}
          itemRanges={ranges}
          existingOptionGroupIds={item.optionGroup.map((og) => og.optionGroupId)}
          handleCreateGroup={closeAssignModalOpenCreateOptionGroupModal}
        />
      )}

      <div className={styles['container']}>
        <div className={styles['heading-container']}>
          <Typography size="xlarge" weight="semi-bold">
            {`${groupTypeLabel.charAt(0).toUpperCase() + groupTypeLabel.slice(1)} groups`}
          </Typography>
          <PriceSchemeSelector value={selectedPriceSchemeId} onChange={setSelectedPriceSchemeId} />
        </div>

        {item.optionGroup.map((optionGroup, index) => (
          <ModifierCategory
            key={`${optionGroup.optionGroupId}-${index}`}
            optionGroup={optionGroup}
            index={index}
            item={item}
            openOptionGroupModal={openModal(ActiveModal.DETAILS)}
            openOptionGroupPricingModal={openModal(ActiveModal.PRICING)}
            openOptionGroupExternalSystemsModal={openModal(ActiveModal.CHANNELS)}
          />
        ))}

        {(item.type === ItemType.STANDARD || item.type === ItemType.BUNDLE || item.type === ItemType.NON_FOOD) && (
          <EmptyStateButton
            label={canAssignOptionGroups ? `+ Add ${groupTypeLabel} group` : `+ Create ${groupTypeLabel} category`}
            onClick={() => openModal(canAssignOptionGroups ? ActiveModal.ASSIGN : ActiveModal.DETAILS)()}
          />
        )}
      </div>
    </>
  );
};

const OptionGroupTab = ({ itemId }: { itemId: string }) => {
  const { selectedPriceSchemeId } = useSelectedPriceScheme();
  const { data: item, isLoading } = useItem(itemId, selectedPriceSchemeId, { enabled: !!selectedPriceSchemeId });

  if (!selectedPriceSchemeId || isLoading) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }
  return <Content item={item} />;
};

export default OptionGroupTab;
